import type { TrackingMeta } from './Trackable';

/**
 * Per-hen-ui-type default {@link TrackingMeta} applied by the binding
 * enhancer when a DSL node does not author its own `_meta.tracking.*`
 * slice. Realises EDUN-10692 AC #3 ("DSL components contain no
 * event-firing code; eventing is provided via TrackingProvider +
 * Trackable") by lifting the per-instance authoring burden off DSL
 * configs onto a single registry keyed by hen-ui component name.
 *
 * Resolution order (see `createBoundRender` and `Trackable`):
 *   1. Author-supplied `_meta.tracking.*` on the DSL node (highest),
 *      including optional `enabled` (Product on/off). Unset `enabled`
 *      keeps the registry Product default on.
 *   2. The default in this map (looked up by component `type`).
 *   3. No tracking — `Trackable` falls through to the placeholder
 *      pass-through and emits nothing.
 *
 * Field-level fields (`fieldName`, `leadFieldAssociated`) are NOT
 * defaulted here — they're per-instance and must be authored on the
 * DSL node when meaningful (Trackable's payload builders accept the
 * undefined fall-through and resolve names from the surrounding step
 * context).
 *
 * Scope of the default registry — interactive form primitives only.
 * Surfaces with ambiguous tracking semantics (Card / Heading / Button
 * / Text / Flex …) are intentionally NOT defaulted here:
 *   - they appear inside both trackable and decorative subtrees
 *   - emitting `ElementViewed` per instance would flood Cohesion with
 *     low-signal events
 *   - existing legacy DSL configs without bindings rely on the "no
 *     `<Trackable>` wrapper, no DSM mount" path (asserted by
 *     `DslConfig.test.tsx — skips the DSM mount entirely`).
 *
 * If a flow needs a non-field surface tracked, the DSL author opts in
 * per-instance with `_meta.tracking: { "kind": "element" }` (or
 * `"product"` / `"submit"`) on that node. Promoting a type into this
 * registry is a deliberate cross-flow decision — not a "we have a
 * Button somewhere" reflex.
 *
 * Net-new product surfaces must be added here for now — `kind` is not yet
 * authorable in the admin editor.
 */
export const COMPONENT_TRACKING_DEFAULTS: Readonly<
  Record<string, TrackingMeta>
> = {
  // Result-page product card. `location` is scoped here (not in
  // `buildProductIdentityFromItem`) so EL cards fall through to the Monarch slot.
  DegreeCardDetailed: { kind: 'product', location: 'dexp-results-page' },

  // Editorial Listing card: `formatType: dexp-ad` (editorial/ad, not an
  // offering) and no `location`, so it falls through to the Monarch slot.
  EditorialListing: {
    kind: 'product',
    formatType: 'dexp-ad',
    formatSubtype: 'dexp-editorial-listings',
  },

  // Bankrate × Credible student-loans banner — DSL `StudentLoansBanner`
  // Puck node (`packages/hen-ui` composite). Auto-emits the product
  // lifecycle via `Trackable` + `createBoundRender` (`onCtaClick` click
  // slot) with affiliate reporting dimensions for Monarch attribution.
  StudentLoansBanner: {
    kind: 'product',
    location: 'student-loans-on-page',
    formatType: 'affiliate',
    formatSubtype: 'credible-student-loans',
    productName: 'credible-student-loans-banner',
    productVariant: 'banner',
  },

  // Bankrate × Credible student-loans inline unit — DSL `StudentLoansInline`
  // Puck node (`packages/hen-ui` composite). Appears on S3 (results page
  // row injection) and S5 (ExitStrategy drawer). Auto-emits the product
  // lifecycle via `Trackable` + `createBoundRender` (`onCtaClick` click
  // slot). Placement `location` differs per surface context and must be
  // authored per-instance via `_meta.tracking.location` on the DSL node
  // (voyager-results-credible for S3, voyager-exit-credible for S5);
  // the registry supplies the shared affiliate identity defaults.
  StudentLoansInline: {
    kind: 'product',
    formatType: 'affiliate',
    formatSubtype: 'credible-student-loans',
    productName: 'credible-student-loans-inline',
    productVariant: 'inline',
  },

  // Atomic form primitives — `packages/hen-ui/src/atomics/index.ts`.
  Select: { kind: 'field', fieldType: 'select' },
  Input: { kind: 'field', fieldType: 'input' },
  Textarea: { kind: 'field', fieldType: 'textarea' },
  Checkbox: { kind: 'field', fieldType: 'checkbox' },
  Switch: { kind: 'field', fieldType: 'switch' },
  // `Radio` (the atomic) owns the whole radio group via `options` —
  // there is no separate `RadioGroup` export. The Puck node `type`
  // emitted by the catalog matches the hen-ui export name.
  Radio: { kind: 'field', fieldType: 'radio' },

  // Composite form primitives — `packages/hen-ui/src/composites/index.ts`.
  // `CheckboxButton` wraps `Checkbox`; `SelectionTile` is an
  // `aria-pressed` toggle. Both are DSL field nodes in tile-style flows.
  CheckboxButton: { kind: 'field', fieldType: 'checkbox' },
  SelectionTile: { kind: 'field', fieldType: 'tile' },
  // Controlled multi-select (`selectedValues` / `onChange`). Form state
  // is the same CSV string ToggleGroup uses; the binding meta in
  // `DslConfig` round-trips it to `string[]`.
  MultiSelect: { kind: 'field', fieldType: 'multi-select' },
};
